import { logConsentEvent } from './observability';

export { getGppData, type GppData } from './gpp';

/**
 * Central subscriber registry for OneTrust consent changes.
 *
 * OneTrust calls a single global `window.OptanonWrapper` on initial load and
 * again whenever the user updates consent. We define that wrapper once (via an
 * inline script in `components/oneTrust.tsx`) and let feature modules register
 * their own handlers through `onConsentChange`.
 *
 * The wrapper script seeds the global queue and snapshot so handlers registered
 * after OneTrust has already fired still receive the current state.
 */

/**
 * Canonical OneTrust consent category IDs. Import these instead of hardcoding
 * `'C0004'` etc. at call sites — keeps every consumer in sync.
 */
export const ONETRUST_CATEGORIES = {
  STRICTLY_NECESSARY: 'C0001',
  PERFORMANCE: 'C0002',
  FUNCTIONAL: 'C0003',
  TARGETING: 'C0004',
} as const;

export type OneTrustCategory = (typeof ONETRUST_CATEGORIES)[keyof typeof ONETRUST_CATEGORIES];

/**
 * Build the OneTrust auto-categorization class name for a script or iframe
 * (e.g. `optanon-category-C0004`). Attach this to the element together with a
 * `data-src` to defer loading until the matching consent group is granted.
 * See: https://my.onetrust.com/s/article/UUID-518074a1-a6da-81c3-be52-bae7685d9c94
 */
export const getOptanonCategoryClass = (category: OneTrustCategory): string => `optanon-category-${category}`;

export type ActiveGroupsHandler = (activeGroups: string[]) => void;

interface ChimeOptanonState {
  lastActiveGroupsString?: string;
  lastActiveGroups?: string[];
}

interface ChimeOptanon {
  handlers: ActiveGroupsHandler[];
  state: ChimeOptanonState;
}

declare global {
  interface Window {
    OptanonWrapper?: () => void;
    OnetrustActiveGroups?: string;
    __CHIME_OPTANON__?: ChimeOptanon;
  }
}

/** Lazily initializes and returns the shared OneTrust runtime container. */
function getOptanon(): ChimeOptanon {
  return (window.__CHIME_OPTANON__ ??= { handlers: [], state: {} });
}

export function parseActiveGroups(value: string | undefined): string[] {
  if (!value) return [];
  return value
    .split(',')
    .map(part => part.trim())
    .filter(Boolean);
}

export function getActiveGroups(): string[] {
  // Detects the browser at runtime; there is no value to parse at a boundary here.
  // oxlint-disable-next-line anti-slop/no-runtime-typeof
  if (typeof window === 'undefined') return [];
  const snapshot = window.__CHIME_OPTANON__?.state.lastActiveGroups;
  // Return a copy — callers must not be able to mutate the internal snapshot.
  if (snapshot) return snapshot.slice();
  return parseActiveGroups(window.OnetrustActiveGroups);
}

export function hasConsent(category: OneTrustCategory): boolean {
  return getActiveGroups().includes(category);
}

/**
 * Register a handler that runs whenever OneTrust reports a consent change.
 * If OneTrust has already fired at least once, the handler is invoked
 * immediately with the current state. Returns an unsubscribe function.
 */
export function onConsentChange(handler: ActiveGroupsHandler): () => void {
  // Detects the browser at runtime; there is no value to parse at a boundary here.
  // oxlint-disable-next-line anti-slop/no-runtime-typeof
  if (typeof window === 'undefined') return () => {};

  const optanon = getOptanon();
  optanon.handlers.push(handler);

  const snapshot = optanon.state.lastActiveGroups;
  if (snapshot) {
    // Copy so a misbehaving handler can't mutate the shared snapshot.
    const snapshotCopy = snapshot.slice();
    try {
      handler(snapshotCopy);
    } catch (err) {
      const message = err instanceof Error ? `${err.name}: ${err.message}` : String(err);
      const stack = err instanceof Error ? err.stack : undefined;
      logConsentEvent({
        event: 'handlerError',
        phase: 'replay',
        activeGroups: snapshotCopy,
        message,
        stack,
      });
    }
  }

  return () => {
    optanon.handlers = optanon.handlers.filter(h => h !== handler);
  };
}
